import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Users, 
  Shuffle,
  Vote,
  Gamepad2,
  LogOut,
  ExternalLink,
  TrendingUp,
  Calendar,
  Building2,
  CreditCard,
  Tag,
  Gift,
  ShoppingCart,
  Scale,
  CheckCircle2,
  Clock,
  AlertCircle,
  Loader2,
  Menu,
  DollarSign,
  Euro,
  BarChart3,
  PieChart,
  X,
  Mail,
  Phone,
  FileText,
  Send,
  XCircle,
  QrCode,
  Sticker,
  Shield,
  Package,
  Settings,
  ToggleLeft,
  ToggleRight
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import AnimatedLogo from "../components/AnimatedLogo";
import { formatDate } from "../lib/utils";
import NotificationBell from "../components/NotificationBell";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [draws, setDraws] = useState([]);
  const [polls, setPolls] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingOrder, setUpdatingOrder] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [serviceConfig, setServiceConfig] = useState(null);
  const [updatingService, setUpdatingService] = useState(null);
  
  // SMTP Configuration
  const [showSmtpModal, setShowSmtpModal] = useState(false);
  const [smtpConfig, setSmtpConfig] = useState({
    smtp_host: "",
    smtp_port: 465,
    smtp_user: "",
    smtp_password: "",
    from_email: "",
    from_name: "TIREO",
    use_ssl: true,
    enabled: false
  });
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  useEffect(() => {
    // Check if user is admin
    if (user && user.email !== "admin@tireo.app") {
      toast.error("Accès réservé aux administrateurs");
      navigate("/dashboard");
      return;
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [statsRes, drawsRes, pollsRes, ordersRes, revenueRes, configRes] = await Promise.all([
        api.admin.getStats(),
        api.admin.getAllDraws(),
        api.admin.getAllPolls(),
        api.admin.getAllOrders(),
        api.admin.getRevenue().catch(() => ({ data: {} })),
        api.admin.getServiceConfig().catch(() => ({ data: {} }))
      ]);
      setStats(statsRes.data);
      setDraws(drawsRes.data);
      setPolls(pollsRes.data);
      setOrders(ordersRes.data);
      setRevenue(revenueRes.data);
      setServiceConfig(configRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    setUpdatingOrder(orderId);
    try {
      await api.admin.updateOrderStatus(orderId, status);
      toast.success("Statut mis à jour");
      loadData();
      // Update selected order if open
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status }));
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setUpdatingOrder(null);
    }
  };

  const handleSaveAdminNotes = async () => {
    if (!selectedOrder) return;
    setSavingNotes(true);
    try {
      await api.admin.updateOrderNotes(selectedOrder.id, adminNotes);
      toast.success("Notes enregistrées");
      setSelectedOrder(prev => ({ ...prev, admin_notes: adminNotes }));
      loadData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSavingNotes(false);
    }
  };

  const openOrderDetail = (order) => {
    setSelectedOrder(order);
    setAdminNotes(order.admin_notes || "");
  };

  const closeOrderDetail = () => {
    setSelectedOrder(null);
    setAdminNotes("");
  };

  const handleToggleService = async (serviceKey) => {
    setUpdatingService(serviceKey);
    try {
      const newConfig = {
        ...serviceConfig,
        [serviceKey]: !serviceConfig[serviceKey]
      };
      await api.admin.updateServiceConfig(newConfig);
      setServiceConfig(newConfig);
      toast.success(`Service ${newConfig[serviceKey] ? "activé" : "désactivé"}`);
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setUpdatingService(null);
    }
  };

  const handleUpdatePrice = async (priceKey, value) => {
    const newValue = parseFloat(value) || 0;
    try {
      const newConfig = {
        ...serviceConfig,
        [priceKey]: newValue
      };
      await api.admin.updateServiceConfig(newConfig);
      setServiceConfig(newConfig);
      toast.success("Tarif mis à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  // SMTP Functions
  const loadSmtpConfig = async () => {
    setSmtpLoading(true);
    try {
      const res = await api.get("/notifications/smtp-config");
      setSmtpConfig(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      console.error("Error loading SMTP config:", error);
    } finally {
      setSmtpLoading(false);
    }
  };

  const saveSmtpConfig = async () => {
    setSmtpLoading(true);
    try {
      await api.put("/notifications/smtp-config", smtpConfig);
      toast.success("Configuration SMTP enregistrée");
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSmtpLoading(false);
    }
  };

  const testSmtpConnection = async () => {
    setSmtpTesting(true);
    try {
      const res = await api.post("/notifications/smtp-test");
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de connexion SMTP");
    } finally {
      setSmtpTesting(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error("Entrez une adresse email");
      return;
    }
    setSmtpTesting(true);
    try {
      const res = await api.post(`/notifications/send-test-email?to_email=${encodeURIComponent(testEmail)}`);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setSmtpTesting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-gradient-to-r from-red-600 to-rose-600 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                <Shuffle className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>TIREO</span>
            </Link>
            <Badge className="bg-white/20 text-white hidden sm:inline-flex">Admin</Badge>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Mobile Menu */}
            <div className="sm:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 px-2">
                    <Menu className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link to="/admin/billing" className="gap-3">
                      <CreditCard className="w-4 h-4 text-green-500" />
                      <span>Facturation TIREO</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/admin/billing" className="gap-3">
                      <Tag className="w-4 h-4 text-purple-500" />
                      <span>Codes promos</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/admin/billing" className="gap-3">
                      <Gift className="w-4 h-4 text-amber-500" />
                      <span>Crédits gratuits</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="gap-3">
                      <BarChart3 className="w-4 h-4 text-blue-500" />
                      <span>Statistiques</span>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium">Administrateur</p>
              <p className="text-xs opacity-80">{user?.email}</p>
            </div>
            <NotificationBell />
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => { setShowSmtpModal(true); loadSmtpConfig(); }} 
              className="text-white hover:bg-white/20"
              title="Paramètres"
            >
              <Settings className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout} className="text-white hover:bg-white/20">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <h1 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8" style={{ fontFamily: 'Outfit' }}>
          Dashboard Administrateur
        </h1>

        {/* Revenue Stats */}
        <Card className="mb-6 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-green-200 dark:border-green-800">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-300">
              <Euro className="w-5 h-5" />
              Chiffre d'affaires TIREO
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white/60 dark:bg-white/10 rounded-xl">
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {revenue?.total_revenue?.toFixed(2) || "0.00"}€
                </p>
                <p className="text-sm text-muted-foreground mt-1">Total général</p>
              </div>
              <div className="text-center p-4 bg-white/60 dark:bg-white/10 rounded-xl">
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {revenue?.total_this_month?.toFixed(2) || "0.00"}€
                </p>
                <p className="text-sm text-muted-foreground mt-1">Ce mois</p>
              </div>
              <div className="text-center p-4 bg-white/60 dark:bg-white/10 rounded-xl">
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {revenue?.total_this_year?.toFixed(2) || "0.00"}€
                </p>
                <p className="text-sm text-muted-foreground mt-1">Cette année</p>
              </div>
              <div className="text-center p-4 bg-white/60 dark:bg-white/10 rounded-xl">
                <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                  {revenue?.services_revenue?.toFixed(2) || "0.00"}€
                </p>
                <p className="text-sm text-muted-foreground mt-1">Services juridiques</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-green-200 dark:border-green-800">
              <div className="flex items-center gap-2 text-sm">
                <PieChart className="w-4 h-4 text-blue-500" />
                <span className="text-muted-foreground">Abonnements:</span>
                <span className="font-medium">{revenue?.subscription_payments || 0}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <BarChart3 className="w-4 h-4 text-purple-500" />
                <span className="text-muted-foreground">Paiements uniques:</span>
                <span className="font-medium">{revenue?.one_time_payments || 0}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Scale className="w-4 h-4 text-amber-500" />
                <span className="text-muted-foreground">Commandes services:</span>
                <span className="font-medium">{revenue?.total_orders || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Platform Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
                  <p className="text-sm text-muted-foreground">Utilisateurs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Shuffle className="w-8 h-8 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{stats?.total_draws || 0}</p>
                  <p className="text-sm text-muted-foreground">Tirages</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Vote className="w-8 h-8 text-purple-500" />
                <div>
                  <p className="text-2xl font-bold">{stats?.total_polls || 0}</p>
                  <p className="text-sm text-muted-foreground">Sondages</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Gamepad2 className="w-8 h-8 text-pink-500" />
                <div>
                  <p className="text-2xl font-bold">{stats?.total_games || 0}</p>
                  <p className="text-sm text-muted-foreground">Jeux</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <ShoppingCart className="w-8 h-8 text-indigo-500" />
                <div>
                  <p className="text-2xl font-bold">{orders.length}</p>
                  <p className="text-sm text-muted-foreground">Commandes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-2xl font-bold">{stats?.completed_draws || 0}</p>
                  <p className="text-sm text-muted-foreground">Terminés</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Admin Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Link to="/admin/billing">
            <Card className="hover:border-primary/50 transition-colors cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">Gestion Facturation</p>
                    <p className="text-sm text-muted-foreground">Infos TIREO, codes promos</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/admin/billing">
            <Card className="hover:border-primary/50 transition-colors cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                    <Tag className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium">Codes Promos</p>
                    <p className="text-sm text-muted-foreground">Créer et gérer les codes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
          
          <Link to="/admin/billing">
            <Card className="hover:border-primary/50 transition-colors cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium">Crédits gratuits</p>
                    <p className="text-sm text-muted-foreground">Attribuer des opérations</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Service Configuration */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configuration des services
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Activez ou désactivez les services et ajustez les tarifs
            </p>
            {serviceConfig && (
              <div className="space-y-6">
                {/* Services toggle */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* QR Code Service */}
                  <div className={`flex items-center justify-between p-4 rounded-xl border-2 transition-colors ${
                    serviceConfig.qrcode_enabled 
                      ? "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800" 
                      : "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700"
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        serviceConfig.qrcode_enabled ? "bg-green-100 dark:bg-green-900" : "bg-slate-200 dark:bg-slate-800"
                      }`}>
                        <QrCode className={`w-5 h-5 ${serviceConfig.qrcode_enabled ? "text-green-600" : "text-slate-400"}`} />
                      </div>
                      <div>
                        <p className="font-medium">QR Code</p>
                        <p className="text-xs text-muted-foreground">Génération gratuite</p>
                      </div>
                    </div>
                    <Switch
                      checked={serviceConfig.qrcode_enabled}
                      onCheckedChange={() => handleToggleService("qrcode_enabled")}
                      disabled={updatingService === "qrcode_enabled"}
                    />
                  </div>

                  {/* Stickers Service */}
                  <div className={`flex items-center justify-between p-4 rounded-xl border-2 transition-colors ${
                    serviceConfig.stickers_enabled 
                      ? "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800" 
                      : "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700"
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        serviceConfig.stickers_enabled ? "bg-purple-100 dark:bg-purple-900" : "bg-slate-200 dark:bg-slate-800"
                      }`}>
                        <Sticker className={`w-5 h-5 ${serviceConfig.stickers_enabled ? "text-purple-600" : "text-slate-400"}`} />
                      </div>
                      <div>
                        <p className="font-medium">Stickers</p>
                        <p className="text-xs text-muted-foreground">Commande personnalisée</p>
                      </div>
                    </div>
                    <Switch
                      checked={serviceConfig.stickers_enabled}
                      onCheckedChange={() => handleToggleService("stickers_enabled")}
                      disabled={updatingService === "stickers_enabled"}
                    />
                  </div>

                  {/* Legal Regulation Service */}
                  <div className={`flex items-center justify-between p-4 rounded-xl border-2 transition-colors ${
                    serviceConfig.legal_regulation_enabled 
                      ? "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800" 
                      : "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700"
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        serviceConfig.legal_regulation_enabled ? "bg-blue-100 dark:bg-blue-900" : "bg-slate-200 dark:bg-slate-800"
                      }`}>
                        <FileText className={`w-5 h-5 ${serviceConfig.legal_regulation_enabled ? "text-blue-600" : "text-slate-400"}`} />
                      </div>
                      <div>
                        <p className="font-medium">Règlement</p>
                        <p className="text-xs text-muted-foreground">Rédaction juridique</p>
                      </div>
                    </div>
                    <Switch
                      checked={serviceConfig.legal_regulation_enabled}
                      onCheckedChange={() => handleToggleService("legal_regulation_enabled")}
                      disabled={updatingService === "legal_regulation_enabled"}
                    />
                  </div>

                  {/* Legal Bailiff Service */}
                  <div className={`flex items-center justify-between p-4 rounded-xl border-2 transition-colors ${
                    serviceConfig.legal_bailiff_enabled 
                      ? "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800" 
                      : "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700"
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        serviceConfig.legal_bailiff_enabled ? "bg-amber-100 dark:bg-amber-900" : "bg-slate-200 dark:bg-slate-800"
                      }`}>
                        <Shield className={`w-5 h-5 ${serviceConfig.legal_bailiff_enabled ? "text-amber-600" : "text-slate-400"}`} />
                      </div>
                      <div>
                        <p className="font-medium">Huissier</p>
                        <p className="text-xs text-muted-foreground">Contrôle officiel</p>
                      </div>
                    </div>
                    <Switch
                      checked={serviceConfig.legal_bailiff_enabled}
                      onCheckedChange={() => handleToggleService("legal_bailiff_enabled")}
                      disabled={updatingService === "legal_bailiff_enabled"}
                    />
                  </div>

                  {/* Legal Bundle Service */}
                  <div className={`flex items-center justify-between p-4 rounded-xl border-2 transition-colors ${
                    serviceConfig.legal_bundle_enabled 
                      ? "bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800" 
                      : "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700"
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        serviceConfig.legal_bundle_enabled ? "bg-indigo-100 dark:bg-indigo-900" : "bg-slate-200 dark:bg-slate-800"
                      }`}>
                        <Package className={`w-5 h-5 ${serviceConfig.legal_bundle_enabled ? "text-indigo-600" : "text-slate-400"}`} />
                      </div>
                      <div>
                        <p className="font-medium">Pack complet</p>
                        <p className="text-xs text-muted-foreground">Règlement + Huissier</p>
                      </div>
                    </div>
                    <Switch
                      checked={serviceConfig.legal_bundle_enabled}
                      onCheckedChange={() => handleToggleService("legal_bundle_enabled")}
                      disabled={updatingService === "legal_bundle_enabled"}
                    />
                  </div>
                </div>

                {/* Pricing Section */}
                <div className="border-t pt-6">
                  <h4 className="font-semibold mb-4 flex items-center gap-2">
                    <Euro className="w-4 h-4 text-green-600" />
                    Tarification (HT)
                  </h4>
                  
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Legal Services Prices */}
                    <div className="space-y-2">
                      <Label className="text-sm flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-500" />
                        Règlement
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={serviceConfig.regulation_price || 99}
                          onChange={(e) => setServiceConfig({...serviceConfig, regulation_price: parseFloat(e.target.value) || 0})}
                          onBlur={(e) => handleUpdatePrice("regulation_price", e.target.value)}
                          className="w-24"
                          min="0"
                          step="0.01"
                        />
                        <span className="text-sm text-muted-foreground">€ HT</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm flex items-center gap-2">
                        <Shield className="w-4 h-4 text-amber-500" />
                        Huissier
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={serviceConfig.bailiff_price || 199}
                          onChange={(e) => setServiceConfig({...serviceConfig, bailiff_price: parseFloat(e.target.value) || 0})}
                          onBlur={(e) => handleUpdatePrice("bailiff_price", e.target.value)}
                          className="w-24"
                          min="0"
                          step="0.01"
                        />
                        <span className="text-sm text-muted-foreground">€ HT</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm flex items-center gap-2">
                        <Package className="w-4 h-4 text-indigo-500" />
                        Pack complet
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={serviceConfig.bundle_price || 249}
                          onChange={(e) => setServiceConfig({...serviceConfig, bundle_price: parseFloat(e.target.value) || 0})}
                          onBlur={(e) => handleUpdatePrice("bundle_price", e.target.value)}
                          className="w-24"
                          min="0"
                          step="0.01"
                        />
                        <span className="text-sm text-muted-foreground">€ HT</span>
                      </div>
                    </div>
                  </div>

                  {/* Sticker Prices */}
                  <h5 className="font-medium mt-6 mb-3 flex items-center gap-2 text-sm">
                    <Sticker className="w-4 h-4 text-purple-500" />
                    Tarifs Stickers
                  </h5>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">5 stickers</Label>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          value={serviceConfig.sticker_price_5 || 15}
                          onChange={(e) => setServiceConfig({...serviceConfig, sticker_price_5: parseFloat(e.target.value) || 0})}
                          onBlur={(e) => handleUpdatePrice("sticker_price_5", e.target.value)}
                          className="w-20 h-8 text-sm"
                          min="0"
                          step="0.01"
                        />
                        <span className="text-xs text-muted-foreground">€</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">10 stickers</Label>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          value={serviceConfig.sticker_price_10 || 25}
                          onChange={(e) => setServiceConfig({...serviceConfig, sticker_price_10: parseFloat(e.target.value) || 0})}
                          onBlur={(e) => handleUpdatePrice("sticker_price_10", e.target.value)}
                          className="w-20 h-8 text-sm"
                          min="0"
                          step="0.01"
                        />
                        <span className="text-xs text-muted-foreground">€</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">50 stickers</Label>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          value={serviceConfig.sticker_price_50 || 80}
                          onChange={(e) => setServiceConfig({...serviceConfig, sticker_price_50: parseFloat(e.target.value) || 0})}
                          onBlur={(e) => handleUpdatePrice("sticker_price_50", e.target.value)}
                          className="w-20 h-8 text-sm"
                          min="0"
                          step="0.01"
                        />
                        <span className="text-xs text-muted-foreground">€</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">100 stickers</Label>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          value={serviceConfig.sticker_price_100 || 140}
                          onChange={(e) => setServiceConfig({...serviceConfig, sticker_price_100: parseFloat(e.target.value) || 0})}
                          onBlur={(e) => handleUpdatePrice("sticker_price_100", e.target.value)}
                          className="w-20 h-8 text-sm"
                          min="0"
                          step="0.01"
                        />
                        <span className="text-xs text-muted-foreground">€</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Service Orders */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Commandes de services ({orders.length})
              {orders.filter(o => o.status === "pending" || o.status === "paid").length > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {orders.filter(o => o.status === "pending" || o.status === "paid").length} à traiter
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {orders.map(order => {
                const statusConfig = {
                  pending_payment: { label: "En attente paiement", color: "bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-300", icon: Clock },
                  pending: { label: "En attente", color: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300", icon: Clock },
                  paid: { label: "Payé", color: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300", icon: CreditCard },
                  processing: { label: "En cours", color: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300", icon: Loader2 },
                  completed: { label: "Terminé", color: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300", icon: CheckCircle2 },
                  cancelled: { label: "Annulé", color: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300", icon: AlertCircle },
                };
                const status = statusConfig[order.status] || statusConfig.pending;
                const StatusIcon = status.icon;
                
                const serviceTypes = {
                  regulation: "Règlement de jeu",
                  bailiff: "Constat d'huissier",
                  bundle: "Pack complet"
                };
                
                return (
                  <div 
                    key={order.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-muted/50 rounded-lg gap-4 cursor-pointer hover:bg-muted/70 transition-colors"
                    onClick={() => openOrderDetail(order)}
                    data-testid={`order-${order.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center">
                        <Scale className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <h4 className="font-medium">{serviceTypes[order.service_type] || order.service_type}</h4>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Building2 className="w-3 h-3" />
                          <span>{order.owner?.company_name || order.owner?.email}</span>
                          <span>•</span>
                          <Calendar className="w-3 h-3" />
                          <span>{formatDate(order.created_at)}</span>
                        </div>
                        {order.draw_title && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Tirage: {order.draw_title}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-3" onClick={(e) => e.stopPropagation()}>
                      <Badge className={status.color}>
                        <StatusIcon className={`w-3 h-3 mr-1 ${order.status === "processing" ? "animate-spin" : ""}`} />
                        {status.label}
                      </Badge>
                      <span className="text-sm font-medium text-green-600">
                        {order.price_ttc || order.price}€
                      </span>
                      
                      {/* Quick status update buttons */}
                      <div className="flex gap-1">
                        {(order.status === "pending" || order.status === "paid") && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateOrderStatus(order.id, "processing")}
                            disabled={updatingOrder === order.id}
                          >
                            {updatingOrder === order.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              "Traiter"
                            )}
                          </Button>
                        )}
                        {order.status === "processing" && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleUpdateOrderStatus(order.id, "completed")}
                            disabled={updatingOrder === order.id}
                          >
                            {updatingOrder === order.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle2 className="w-4 h-4 mr-1" />
                                Terminé
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {orders.length === 0 && (
                <p className="text-center text-muted-foreground py-8">Aucune commande de service</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Order Detail Modal */}
        <Dialog open={!!selectedOrder} onOpenChange={(open) => !open && closeOrderDetail()}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Scale className="w-5 h-5 text-indigo-600" />
                Détail de la commande
              </DialogTitle>
              <DialogDescription>
                Gérez cette commande de service juridique
              </DialogDescription>
            </DialogHeader>
            
            {selectedOrder && (
              <div className="space-y-6 mt-4">
                {/* Service Info */}
                <div className="bg-indigo-50 dark:bg-indigo-950/30 rounded-xl p-4">
                  <h3 className="font-semibold text-lg mb-2">
                    {{
                      regulation: "Rédaction de règlement",
                      bailiff: "Constat d'huissier",
                      bundle: "Pack complet (Règlement + Huissier)"
                    }[selectedOrder.service_type] || selectedOrder.service_type}
                  </h3>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <span className="flex items-center gap-1">
                      <Euro className="w-4 h-4 text-green-500" />
                      <strong>{selectedOrder.price_ttc || selectedOrder.price}€ TTC</strong>
                      {selectedOrder.price_ht && (
                        <span className="text-muted-foreground">({selectedOrder.price_ht}€ HT)</span>
                      )}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      Commandé le {formatDate(selectedOrder.created_at)}
                    </span>
                  </div>
                  {selectedOrder.draw_title && (
                    <p className="mt-2 text-sm">
                      <FileText className="w-4 h-4 inline mr-1 text-purple-500" />
                      Tirage associé: <strong>{selectedOrder.draw_title}</strong>
                    </p>
                  )}
                  {selectedOrder.draw_date && (
                    <p className="text-sm text-muted-foreground">
                      Date du tirage: {selectedOrder.draw_date}
                    </p>
                  )}
                  {selectedOrder.notes && (
                    <p className="mt-2 text-sm bg-white/50 dark:bg-black/20 p-2 rounded">
                      <strong>Notes client:</strong> {selectedOrder.notes}
                    </p>
                  )}
                </div>

                {/* Client Info */}
                <div className="bg-blue-50 dark:bg-blue-950/30 rounded-xl p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-600" />
                    Coordonnées du client
                  </h4>
                  <div className="space-y-2">
                    <p className="font-medium text-lg">
                      {selectedOrder.owner?.company_name || "Nom non renseigné"}
                    </p>
                    {selectedOrder.owner?.contact_name && (
                      <p className="text-sm text-muted-foreground">
                        Contact: {selectedOrder.owner.contact_name}
                      </p>
                    )}
                    {selectedOrder.owner?.email && (
                      <a 
                        href={`mailto:${selectedOrder.owner.email}`}
                        className="flex items-center gap-2 text-blue-600 hover:underline"
                      >
                        <Mail className="w-4 h-4" />
                        {selectedOrder.owner.email}
                      </a>
                    )}
                    {selectedOrder.owner?.phone && (
                      <a 
                        href={`tel:${selectedOrder.owner.phone}`}
                        className="flex items-center gap-2 text-green-600 hover:underline"
                      >
                        <Phone className="w-4 h-4" />
                        {selectedOrder.owner.phone}
                      </a>
                    )}
                    {selectedOrder.owner?.address && (
                      <p className="text-sm text-muted-foreground">
                        <Building2 className="w-4 h-4 inline mr-1" />
                        {selectedOrder.owner.address}
                      </p>
                    )}
                    {selectedOrder.owner?.organization_type && (
                      <Badge variant="secondary">
                        {selectedOrder.owner.organization_type === "enterprise" ? "Entreprise" : "Association"}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Status Management */}
                <div className="bg-amber-50 dark:bg-amber-950/30 rounded-xl p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-600" />
                    Statut de la commande
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant={selectedOrder.status === "pending" || selectedOrder.status === "paid" ? "default" : "outline"}
                      disabled={selectedOrder.status === "pending" || selectedOrder.status === "paid" || updatingOrder}
                      className={selectedOrder.status === "pending" || selectedOrder.status === "paid" ? "bg-amber-500" : ""}
                    >
                      <Clock className="w-4 h-4 mr-1" />
                      En attente
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedOrder.status === "processing" ? "default" : "outline"}
                      onClick={() => handleUpdateOrderStatus(selectedOrder.id, "processing")}
                      disabled={selectedOrder.status === "processing" || updatingOrder}
                      className={selectedOrder.status === "processing" ? "bg-indigo-500" : ""}
                    >
                      {updatingOrder === selectedOrder.id ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-1" />
                      )}
                      En cours
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedOrder.status === "completed" ? "default" : "outline"}
                      onClick={() => handleUpdateOrderStatus(selectedOrder.id, "completed")}
                      disabled={selectedOrder.status === "completed" || updatingOrder}
                      className={selectedOrder.status === "completed" ? "bg-green-500" : ""}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Terminé
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedOrder.status === "cancelled" ? "destructive" : "outline"}
                      onClick={() => handleUpdateOrderStatus(selectedOrder.id, "cancelled")}
                      disabled={selectedOrder.status === "cancelled" || updatingOrder}
                      className={selectedOrder.status === "cancelled" ? "" : "text-red-600 hover:bg-red-50 dark:hover:bg-red-950"}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Annulé
                    </Button>
                  </div>
                </div>

                {/* Admin Notes */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Notes administrateur (internes)
                  </Label>
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Ajoutez des notes pour le suivi de cette commande..."
                    rows={3}
                  />
                  <Button
                    size="sm"
                    onClick={handleSaveAdminNotes}
                    disabled={savingNotes || adminNotes === (selectedOrder.admin_notes || "")}
                  >
                    {savingNotes ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                    )}
                    Enregistrer les notes
                  </Button>
                </div>

                {/* Quick Actions */}
                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  {selectedOrder.owner?.email && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.location.href = `mailto:${selectedOrder.owner.email}?subject=Votre commande de service juridique TIREO`}
                    >
                      <Mail className="w-4 h-4 mr-1" />
                      Envoyer un email
                    </Button>
                  )}
                  {selectedOrder.owner?.phone && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.location.href = `tel:${selectedOrder.owner.phone}`}
                    >
                      <Phone className="w-4 h-4 mr-1" />
                      Appeler
                    </Button>
                  )}
                  {selectedOrder.draw_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/draw/${selectedOrder.draw_id}/show`, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Voir le tirage
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* All Draws */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shuffle className="w-5 h-5" />
              Tous les tirages ({draws.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {draws.map(draw => (
                <div 
                  key={draw.id}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Shuffle className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium">{draw.title}</h4>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Building2 className="w-3 h-3" />
                        <span>{draw.owner?.company_name || draw.organizer_name}</span>
                        <span>•</span>
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(draw.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Badge variant={draw.status === "completed" ? "default" : "secondary"}>
                      {draw.status === "completed" ? "Terminé" : "En cours"}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {draw.participants?.length || 0} participants
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => window.open(`/draw/${draw.id}/show`, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {draws.length === 0 && (
                <p className="text-center text-muted-foreground py-8">Aucun tirage</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* All Polls */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Vote className="w-5 h-5" />
              Tous les sondages ({polls.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {polls.map(poll => (
                <div 
                  key={poll.id}
                  className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                      <Vote className="w-5 h-5 text-purple-500" />
                    </div>
                    <div>
                      <h4 className="font-medium">{poll.title}</h4>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Building2 className="w-3 h-3" />
                        <span>{poll.owner?.company_name || poll.organizer_name}</span>
                        <span>•</span>
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(poll.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Badge variant={poll.status === "closed" ? "default" : "secondary"}>
                      {poll.status === "closed" ? "Fermé" : "Ouvert"}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {poll.total_votes || 0} votes
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => window.open(`/poll/${poll.id}/results`, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              {polls.length === 0 && (
                <p className="text-center text-muted-foreground py-8">Aucun sondage</p>
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* SMTP Configuration Modal */}
      <Dialog open={showSmtpModal} onOpenChange={setShowSmtpModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-500" />
              Configuration SMTP
            </DialogTitle>
            <DialogDescription>
              Configurez le serveur SMTP pour les notifications par email (port 465 avec SSL)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <Label className="text-base font-semibold">Activer les notifications email</Label>
                <p className="text-sm text-muted-foreground">Les emails seront envoyés pour les événements importants</p>
              </div>
              <Switch
                checked={smtpConfig.enabled}
                onCheckedChange={(checked) => setSmtpConfig(prev => ({ ...prev, enabled: checked }))}
              />
            </div>

            {/* SMTP Settings */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Serveur SMTP</Label>
                <Input
                  placeholder="smtp.example.com"
                  value={smtpConfig.smtp_host}
                  onChange={(e) => setSmtpConfig(prev => ({ ...prev, smtp_host: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Port</Label>
                <Input
                  type="number"
                  placeholder="465"
                  value={smtpConfig.smtp_port}
                  onChange={(e) => setSmtpConfig(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 465 }))}
                />
                <p className="text-xs text-muted-foreground">Port 465 = SSL, Port 587 = TLS</p>
              </div>
              <div className="space-y-2">
                <Label>Utilisateur SMTP</Label>
                <Input
                  placeholder="user@example.com"
                  value={smtpConfig.smtp_user}
                  onChange={(e) => setSmtpConfig(prev => ({ ...prev, smtp_user: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Mot de passe</Label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={smtpConfig.smtp_password}
                  onChange={(e) => setSmtpConfig(prev => ({ ...prev, smtp_password: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Email expéditeur</Label>
                <Input
                  placeholder="noreply@tireo.app"
                  value={smtpConfig.from_email}
                  onChange={(e) => setSmtpConfig(prev => ({ ...prev, from_email: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Nom expéditeur</Label>
                <Input
                  placeholder="TIREO"
                  value={smtpConfig.from_name}
                  onChange={(e) => setSmtpConfig(prev => ({ ...prev, from_name: e.target.value }))}
                />
              </div>
            </div>

            {/* SSL Option */}
            <div className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
              <Switch
                checked={smtpConfig.use_ssl}
                onCheckedChange={(checked) => setSmtpConfig(prev => ({ ...prev, use_ssl: checked }))}
              />
              <div>
                <Label className="text-sm font-medium">Utiliser SSL (Port 465)</Label>
                <p className="text-xs text-muted-foreground">Désactivez pour utiliser TLS (Port 587)</p>
              </div>
            </div>

            {/* Test Section */}
            <div className="border-t pt-4 space-y-3">
              <h4 className="font-semibold flex items-center gap-2">
                <Send className="w-4 h-4" />
                Tester la configuration
              </h4>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={testSmtpConnection}
                  disabled={smtpTesting || !smtpConfig.smtp_host}
                >
                  {smtpTesting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Tester la connexion
                </Button>
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Email de test"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="flex-1"
                />
                <Button
                  onClick={sendTestEmail}
                  disabled={smtpTesting || !testEmail || !smtpConfig.smtp_host}
                >
                  {smtpTesting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Envoyer
                </Button>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end gap-3 border-t pt-4">
              <Button variant="outline" onClick={() => setShowSmtpModal(false)}>
                Annuler
              </Button>
              <Button onClick={saveSmtpConfig} disabled={smtpLoading}>
                {smtpLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
